import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { ClientFlagControl } from '@/components/clientFlags/ClientFlagControl';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { ClientConfigForm } from '@/components/config/ClientConfigForm';
import { ClientNotesField } from '@/components/config/ClientNotesField';
import { ConfigHygienePanel } from '@/components/config/ConfigHygienePanel';
import { configFileFor, describeConfigFileError, readConfigFile } from '@/components/config/configFile';
import { JsonDiffView } from '@/components/config/JsonDiffView';
import { QuotaCheckCard } from '@/components/tools/QuotaCheckCard';
import { ToolPage } from '@/components/ui/ToolPage';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientService } from '@/services/clientService';
import {
  CONTRACT_DISPLAY,
  EMPTY_CONTRACT,
  canonicalPartner,
  isContract,
  normalisePartner,
  partnerNames,
  resolveContract,
  type ContractFormState,
} from '@/config/contract';
import { clientMetadataService, type MetadataFields } from '@/services/clientMetadataService';
import { Client, ClientConfig } from '@/types';
import { FileExportService } from '@/utils/fileExport';
import './UpdateClientConfig.css';

/**
 * What the page keeps about the client in the portal's own table, as the form
 * holds it: the contract part (drawn in the config form's Account section) and
 * the notes (on their own tab). An empty string is "none".
 */
interface RecordFields extends ContractFormState {
  notes: string;
}

/** The four views of a client. Their buttons sit in the client card, beside the client's name. */
const UPDATE_TABS = [
  { key: 'config', label: 'Config' },
  { key: 'metadata', label: 'Notes and tags' },
  { key: 'quotas', label: 'Bedrock limits' },
  { key: 'json', label: 'JSON file' },
] as const;
type UpdateTab = (typeof UPDATE_TABS)[number]['key'];

export default function UpdateClientConfig() {
  const [searchParams] = useSearchParams();

  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  // The picker is tall. Once a client is chosen it folds to one line, so the
  // form starts near the top of the page.
  const [pickerOpen, setPickerOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<UpdateTab>('config');
  // The slot in the left column where the config form puts its rail.
  const [railHost, setRailHost] = useState<HTMLDivElement | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Unsaved form edits — hygiene-panel removal re-hydrates the form, so it is
  // blocked while true to avoid silently discarding the operator's changes.
  const [formDirty, setFormDirty] = useState(false);
  // Bumped on every successful save so the form shows its green "Saved" note.
  const [saveCounter, setSaveCounter] = useState(0);

  // Pending diff from the shared form, shown in the confirm modal
  const [pendingUpdates, setPendingUpdates] = useState<Partial<ClientConfig> | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // The client's record in the portal's own table (not deployed)
  const [contract, setContract] = useState<ContractFormState>(EMPTY_CONTRACT);
  const [metaNotes, setMetaNotes] = useState('');
  // The fields as the table held them when the form was filled. Null until that read is
  // back. A save writes only the fields that differ from this, so a form that never loaded,
  // or that nobody touched, writes nothing.
  const [metaBaseline, setMetaBaseline] = useState<RecordFields | null>(null);
  // The contract the form opened on. It can come from the config alone (the row holds none yet):
  // a save then writes it to the row, but opening the page is not an edit.
  const [openedContract, setOpenedContract] = useState<RecordFields['contract']>('');
  const [metaLoadError, setMetaLoadError] = useState<string | null>(null);
  const [metaLoadKey, setMetaLoadKey] = useState(0);
  const flags = useClientFlags();
  // The partners named on any client, for the partner picker.
  const knownPartners = partnerNames(flags.metadata.values());

  // Developer JSON replace
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [uploadedConfig, setUploadedConfig] = useState<ClientConfig | null>(null);
  const [showReplacePreview, setShowReplacePreview] = useState(false);

  // Prefill selected client from query params (if present)
  useEffect(() => {
    const qClient = (searchParams.get('clientName') || '').trim();
    if (qClient) {
      setSelectedClientName(qClient);
      setPickerOpen(false);
    }
  }, []);

  const loadClients = async () => {
    try {
      setWorking(true);
      const list = await clientService.getAllClients();
      setClients(list);
    } catch (e) {
      const detail = e instanceof Error ? ` ${e.message}` : '';
      setError(`Could not load the client list. Reload the page to try again.${detail}`);
    } finally {
      setWorking(false);
      setClientsLoaded(true);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const selectedClient = clients.find((c) => c.name === selectedClientName);

  // Fill the contract and notes from the selected client's row, read from the table this
  // instant. A row with no contract yet shows the billing its config holds, when it holds one.
  useEffect(() => {
    if (!selectedClientName || !selectedClient) return;
    let cancelled = false;
    setMetaBaseline(null);
    setMetaLoadError(null);
    clientMetadataService
      .getMetadataFresh(selectedClientName)
      .then((meta) => {
        if (cancelled) return;
        const loaded: RecordFields = {
          contract: resolveContract(meta, selectedClient.config) ?? '',
          partner: meta?.partner || '',
          trialStartDate: meta?.trialStartDate || '',
          trialEndDate: meta?.trialEndDate || '',
          notes: meta?.notes || '',
        };
        setContract({
          contract: loaded.contract,
          partner: loaded.partner,
          trialStartDate: loaded.trialStartDate,
          trialEndDate: loaded.trialEndDate,
        });
        setMetaNotes(loaded.notes);
        // The baseline is the row as stored. A contract read from the config alone is not on
        // the row yet, so the first save writes it there.
        setMetaBaseline({ ...loaded, contract: meta?.contract ?? '' });
        setOpenedContract(loaded.contract);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Could not read the contract and notes', e);
        setMetaLoadError(e instanceof Error ? e.message : 'The read failed.');
      });
    return () => {
      cancelled = true;
    };
    // The config is in the list as soon as the client is; re-reading on each config patch is not needed.
  }, [selectedClientName, !!selectedClient, metaLoadKey]);

  // The form is long and its save button stays in view, so a message at the
  // top of the page can land off screen. Bring it to the person.
  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error || success) messagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error, success]);

  const handleSelectClient = (name: string) => {
    setSelectedClientName(name);
    setPickerOpen(!name);
  };

  const handleFormSubmit = (updates: Partial<ClientConfig>) => {
    setError(null);
    setSuccess(null);
    if (!selectedClientName) {
      setError('Choose a client first.');
      return;
    }
    setPendingUpdates(updates);
    setShowPreview(true);
  };

  /**
   * Apply a saved diff to the locally-held client list. DynamoDB scans are
   * eventually consistent, so refetching immediately after a write can return
   * the OLD record — the form would rehydrate stale and keep showing pending
   * changes. Patching locally from what we just wrote is deterministic; the
   * service cache was already busted so other pages refetch fresh later.
   * `undefined` values mean the key was REMOVEd.
   */
  const patchSelectedClientConfig = (updates: Partial<ClientConfig>) => {
    setClients((prev) =>
      prev.map((c) => {
        if (c.name !== selectedClientName) return c;
        const cfg: Record<string, unknown> = { ...c.config };
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) delete cfg[key];
          else cfg[key] = value;
        }
        return { ...c, config: cfg as ClientConfig };
      })
    );
  };

  /**
   * The record fields as a save writes them. A partner is saved in the spelling
   * other clients use. Only a Partner keeps a partner, and only a Trial keeps
   * trial dates: changing the contract clears what no longer applies.
   */
  const recordNow = (): RecordFields => {
    const partner =
      contract.contract === 'partner' ? canonicalPartner(normalisePartner(contract.partner), knownPartners) : '';
    const trial = contract.contract === 'trial';
    return {
      contract: contract.contract,
      partner,
      trialStartDate: trial ? contract.trialStartDate : '',
      trialEndDate: trial ? contract.trialEndDate : '',
      notes: metaNotes,
    };
  };

  /**
   * The record fields the person changed, and nothing else. Each write names
   * its fields, so the rest of the client's row (its organisation, its two
   * tags) is never touched from this page.
   */
  const changedRecordFields = (): MetadataFields => {
    if (!metaBaseline) return {};
    const now = recordNow();
    const changed: MetadataFields = {};
    if (now.contract && now.contract !== metaBaseline.contract) changed.contract = now.contract;
    if (now.partner !== metaBaseline.partner) changed.partner = now.partner || null;
    if (now.trialStartDate !== metaBaseline.trialStartDate) changed.trialStartDate = now.trialStartDate || null;
    if (now.trialEndDate !== metaBaseline.trialEndDate) changed.trialEndDate = now.trialEndDate || null;
    if (now.notes !== metaBaseline.notes) changed.notes = now.notes || null;
    return changed;
  };

  /** Write the changed record fields. Returns how many fields were written. */
  const writeRecordFields = async (): Promise<number> => {
    const changed = changedRecordFields();
    const count = Object.keys(changed).length;
    if (count === 0) return 0;
    await clientMetadataService.updateFields(selectedClientName, changed);
    setMetaBaseline(recordNow());
    return count;
  };

  /** Save the notes alone, from their tab. The contract saves with the config. */
  const saveNotes = async () => {
    if (!metaBaseline) return;
    try {
      setWorking(true);
      setError(null);
      if (metaNotes === metaBaseline.notes) {
        setSuccess(`Nothing to save: the notes for ${selectedClientName} are as they were.`);
        return;
      }
      await clientMetadataService.updateFields(selectedClientName, { notes: metaNotes || null });
      setMetaBaseline({ ...metaBaseline, notes: metaNotes });
      setSuccess(`Saved the notes for ${selectedClientName}.`);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The notes were not saved.${detail}`);
    } finally {
      setWorking(false);
    }
  };

  const saveChanges = async () => {
    if (!pendingUpdates) return;
    const count = Object.keys(pendingUpdates).length;
    const recordCount = Object.keys(changedRecordFields()).length;
    try {
      setWorking(true);
      if (count > 0) await clientService.updateClientConfig(selectedClientName, pendingUpdates);
      // The contract and notes save with the config. The config is saved by now, so a
      // failure here is said beside the success, never in place of it.
      try {
        await writeRecordFields();
      } catch (metaErr) {
        console.error('Failed to save the contract alongside the config:', metaErr);
        const detail = metaErr instanceof Error ? ` ${metaErr.message}` : '';
        setError(`The config was saved, but the contract and notes were not. Save again to write them.${detail}`);
      }
      patchSelectedClientConfig(pendingUpdates);
      setSaveCounter((c) => c + 1);
      setSuccess(
        count === 0 && recordCount === 0
          ? `No setting changed for ${selectedClientName}.`
          : count === 0
            ? `Saved the contract for ${selectedClientName}.`
            : `Saved ${count} ${count === 1 ? 'change' : 'changes'} to ${selectedClientName}. The client picks ${count === 1 ? 'it' : 'them'} up at its next deploy.`
      );
      setShowPreview(false);
      setPendingUpdates(null);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The changes were not saved.${detail}`);
      setShowPreview(false);
    } finally {
      setWorking(false);
    }
  };

  const replaceConfig = async () => {
    if (!uploadedConfig || !selectedClientName) return;
    try {
      setWorking(true);
      await clientService.replaceClientConfig(selectedClientName, uploadedConfig);
      // Full put — swap the whole local config for the uploaded one
      setClients((prev) => prev.map((c) => (c.name === selectedClientName ? { ...c, config: uploadedConfig } : c)));
      setSaveCounter((c) => c + 1);
      setSuccess(
        `Replaced the whole config for ${selectedClientName} with the file. The client picks it up at its next deploy.`
      );
      setShowReplacePreview(false);
      setUploadedConfig(null);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The config was not replaced.${detail}`);
      setShowReplacePreview(false);
    } finally {
      setWorking(false);
    }
  };

  /** The record changes the confirm step lists: "Contract: Legacy to Partner". */
  const RECORD_LABELS: Record<keyof RecordFields, string> = {
    contract: 'Contract',
    partner: 'Partner',
    trialStartDate: 'Trial start',
    trialEndDate: 'Trial end',
    notes: 'Notes',
  };
  const recordChanges = metaBaseline
    ? (Object.keys(changedRecordFields()) as (keyof RecordFields)[]).map((key) => {
        const words = (value: string) =>
          !value ? '(none)' : key === 'contract' && isContract(value) ? CONTRACT_DISPLAY[value].label : value;
        const before = words(metaBaseline[key]);
        const after = words(recordNow()[key]);
        return key === 'notes' ? 'Notes: changed' : `${RECORD_LABELS[key]}: ${before} to ${after}`;
      })
    : [];

  const showSummary = !!selectedClient && !pickerOpen;

  // One place for the page's messages: the right column once a client is open, else under the picker.
  const messages = (
    <div ref={messagesRef}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
    </div>
  );

  return (
    <ToolPage
      title="Update client config"
      subtitle="Change a client's region, apps or feature switches. A change takes effect at the client's next deploy."
    >
      {/* The picker, full width, while no client is chosen or the person is changing it. */}
      {!showSummary && (
        <Card className="mb-3">
          <Card.Body>
            {!clientsLoaded ? (
              <div className="tool-form__hint">
                <Spinner size="sm" className="me-2" />
                Loading clients
              </div>
            ) : (
              <>
                <h2 className="section-label">Client</h2>
                <ClientSelectGroup value={selectedClientName} onChange={handleSelectClient} clients={clients} />
                {selectedClientName && !selectedClient && (
                  <p className="tool-form__hint mt-2 mb-0">
                    No client named <span className="mono">{selectedClientName}</span> has a config. Choose another, or{' '}
                    <Link to={`/tools/create-client-config?clientName=${encodeURIComponent(selectedClientName)}`}>
                      create its config
                    </Link>
                    .
                  </p>
                )}
                {selectedClient && (
                  <div className="update-config__keep">
                    {formDirty && (
                      <span className="tool-form__hint">
                        You have unsaved changes for {selectedClient.name}. Choosing another client discards them.
                      </span>
                    )}
                    <Button variant="outline-secondary" size="sm" onClick={() => setPickerOpen(false)}>
                      Keep {selectedClient.name}
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {!(selectedClientName && selectedClient) && messages}

      {selectedClientName && selectedClient && (
        <Row>
          {/* The left column: the client, its four views, and the config form's rail. */}
          <Col md={4} lg={3} xl={2} className="mb-3">
            <div className="client-config-side">
              <Card>
                <Card.Body className="client-config-side__client">
                  <div>
                    <h2 className="section-label mb-1">Client</h2>
                    <div className="client-config-side__name">
                      {selectedClient.name}
                      {selectedClient.config.devInstance && <Badge bg="warning">Dev</Badge>}
                      <ClientTags clientName={selectedClient.name} flags={flags} />
                    </div>
                  </div>
                  <dl className="client-config-side__facts">
                    <div>
                      <dt>AWS account</dt>
                      <dd className="mono">{selectedClient.config.clientAccountId}</dd>
                    </div>
                    <div>
                      <dt>Region</dt>
                      <dd className="mono">{selectedClient.config.region}</dd>
                    </div>
                  </dl>
                  <Nav
                    variant="pills"
                    className="nav-pills-custom client-config-side__views"
                    activeKey={activeTab}
                    onSelect={(key) => key && setActiveTab(key as UpdateTab)}
                    aria-label="What to change"
                  >
                    {UPDATE_TABS.map((t) => (
                      <Nav.Item key={t.key}>
                        <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                  {showSummary && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setPickerOpen(true)}>
                      Change client
                    </Button>
                  )}
                </Card.Body>
              </Card>
              <div ref={setRailHost} />
            </div>
          </Col>
          <Col md={8} lg={9} xl={10}>
            {messages}
            <Tab.Container activeKey={activeTab}>
              <Tab.Content>
                {/* Tab 1: the config form */}
                <Tab.Pane eventKey="config">
                  <ConfigHygienePanel
                    clientName={selectedClientName}
                    config={selectedClient.config}
                    disabled={working || formDirty}
                    disabledNote={
                      formDirty
                        ? 'Save your changes to the form first. Removing a setting reloads the form, and that discards them.'
                        : undefined
                    }
                    onCleaned={(keys) =>
                      patchSelectedClientConfig(
                        Object.fromEntries(keys.map((k) => [k, undefined])) as Partial<ClientConfig>
                      )
                    }
                    onError={setError}
                    onSuccess={setSuccess}
                  />

                  {metaLoadError && (
                    <Alert variant="danger">
                      The contract could not be read, so the form cannot save yet. {metaLoadError}{' '}
                      <Button
                        variant="link"
                        className="p-0 align-baseline"
                        onClick={() => setMetaLoadKey((k) => k + 1)}
                      >
                        Try again
                      </Button>
                    </Alert>
                  )}
                  <ClientConfigForm
                    mode="update"
                    currentConfig={selectedClient.config}
                    working={working}
                    submitLabel="Review and save"
                    submitDisabled={!metaBaseline}
                    submitHint="A save also writes any on-by-default switch the config does not hold yet."
                    onSubmit={handleFormSubmit}
                    onError={setError}
                    onDirtyChange={setFormDirty}
                    saveCounter={saveCounter}
                    contract={contract}
                    onContractChange={setContract}
                    partnerNames={knownPartners}
                    railHost={activeTab === 'config' ? railHost : null}
                    extraPendingCount={
                      Object.keys(changedRecordFields()).filter(
                        (k) => k !== 'notes' && !(k === 'contract' && contract.contract === openedContract)
                      ).length
                    }
                  />
                </Tab.Pane>

                {/* Tab 2: notes and the two tags */}
                <Tab.Pane eventKey="metadata">
                  <Card>
                    <Card.Body>
                      <div className="tool-form">
                        <section className="tool-form__section">
                          <h2 className="section-label">Notes</h2>
                          <p className="tool-form__hint">
                            What the team should know about the client. Notes are not deployed. The contract is in the
                            Account section of the Config tab.
                          </p>
                          {metaLoadError ? (
                            <Alert variant="danger" className="mb-0">
                              The notes could not be read, so they cannot be changed here yet. {metaLoadError}{' '}
                              <Button
                                variant="link"
                                className="p-0 align-baseline"
                                onClick={() => setMetaLoadKey((k) => k + 1)}
                              >
                                Try again
                              </Button>
                            </Alert>
                          ) : !metaBaseline ? (
                            <p className="tool-form__hint">
                              <Spinner as="span" size="sm" className="me-2" />
                              Reading the notes
                            </p>
                          ) : (
                            <ClientNotesField notes={metaNotes} onNotesChange={setMetaNotes} />
                          )}
                        </section>
                        <div className="tool-form__actions">
                          <Button variant="primary" disabled={working || !metaBaseline} onClick={saveNotes}>
                            {working ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Saving
                              </>
                            ) : (
                              'Save notes'
                            )}
                          </Button>
                        </div>
                        <section className="tool-form__section">
                          <h2 className="section-label">Deploys and state</h2>
                          <p className="tool-form__hint">
                            These two are saved when you confirm each one. They do not wait for a save button.
                          </p>
                          <dl className="update-config__flags">
                            <div>
                              <dt>Deploys</dt>
                              <dd>
                                <ClientFlagControl
                                  kind="deploy-block"
                                  clientName={selectedClientName}
                                  disabled={working}
                                />
                              </dd>
                            </div>
                            <div>
                              <dt>State</dt>
                              <dd>
                                <ClientFlagControl kind="inactive" clientName={selectedClientName} disabled={working} />
                              </dd>
                            </div>
                          </dl>
                        </section>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Tab 3: Bedrock limits for the account */}
                <Tab.Pane eventKey="quotas">
                  <QuotaCheckCard
                    accountId={selectedClient.config.clientAccountId}
                    region={selectedClient.config.region || 'us-east-1'}
                    disabled={working}
                  />
                </Tab.Pane>

                {/* Tab 4: replace the whole config from a JSON file */}
                <Tab.Pane eventKey="json">
                  <Card>
                    <Card.Body>
                      <div className="tool-form">
                        <section className="tool-form__section">
                          <h2 className="section-label">Replace from a JSON file</h2>
                          <p className="tool-form__hint">
                            For developers. Upload a whole config as JSON to replace this client's config. A setting
                            that is not in the file is removed. Download the current config first to edit it.
                          </p>
                          <Form.Group controlId="update-json-file">
                            <Form.Label>Config file</Form.Label>
                            <Form.Control
                              type="file"
                              accept="application/json,.json"
                              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                setJsonError(null);
                                setUploadedConfig(null);
                                const file = e.currentTarget.files?.[0];
                                if (!file) return;
                                try {
                                  setUploadedConfig(await readConfigFile(file));
                                } catch (err: unknown) {
                                  setJsonError(describeConfigFileError(err));
                                }
                              }}
                            />
                            <Form.Text>A complete config. A setting the portal does not know is refused.</Form.Text>
                          </Form.Group>
                          {jsonError && (
                            <Alert variant="danger" className="mt-3 mb-0">
                              {jsonError}
                            </Alert>
                          )}
                          {uploadedConfig && (
                            <p className="tool-form__hint mt-3">
                              The file is valid and holds {Object.keys(uploadedConfig).length} settings.
                            </p>
                          )}
                        </section>
                        <div className="tool-form__actions">
                          <Button
                            variant="primary"
                            disabled={!uploadedConfig || !selectedClientName || working}
                            onClick={() => {
                              setJsonError(null);
                              setShowReplacePreview(true);
                            }}
                          >
                            Review and replace
                          </Button>
                          <Button
                            variant="outline-secondary"
                            disabled={!selectedClient}
                            onClick={() => {
                              if (!selectedClient) return;
                              FileExportService.downloadFile(configFileFor(selectedClient.name, selectedClient.config));
                            }}
                          >
                            <Download className="me-1" aria-hidden="true" />
                            Download current config
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Col>
        </Row>
      )}

      <Modal show={showPreview} onHide={() => !working && setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton={!working}>
          <Modal.Title>Save these changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This changes the stored config for <strong>{selectedClientName}</strong>. Nothing is deployed now: the
            client picks the change up at its next deploy.
          </p>
          {recordChanges.length > 0 && (
            <div className="mb-3">
              <h3 className="section-label">The portal's record of the client</h3>
              <ul className="mb-0">
                {recordChanges.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          <JsonDiffView
            before={selectedClient?.config as Record<string, unknown> | undefined}
            after={
              pendingUpdates
                ? { ...((selectedClient?.config as Record<string, unknown>) || {}), ...pendingUpdates }
                : undefined
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPreview(false)} disabled={working}>
            Cancel
          </Button>
          <Button variant="primary" disabled={working || !pendingUpdates} onClick={saveChanges}>
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showReplacePreview} onHide={() => !working && setShowReplacePreview(false)} size="lg" centered>
        <Modal.Header closeButton={!working}>
          <Modal.Title>Replace the whole config?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This replaces every setting for <strong>{selectedClientName || 'the client'}</strong> with the file. A
            setting that is not in the file is removed. Nothing is deployed now: the client picks the change up at its
            next deploy.
          </p>
          <JsonDiffView
            before={selectedClient?.config as Record<string, unknown> | undefined}
            after={(uploadedConfig ?? undefined) as Record<string, unknown> | undefined}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowReplacePreview(false)} disabled={working}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={working || !uploadedConfig || !selectedClientName}
            onClick={replaceConfig}
          >
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Replacing
              </>
            ) : (
              'Replace config'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
